import KtrlLayout from "../../components/ktrl/KtrlLayout";

const Tag = ({ children }) => (
  <span className="inline-block text-xs font-semibold uppercase tracking-widest text-indigo-600 mb-3">
    {children}
  </span>
);

const H2 = ({ no, en }) => (
  <h2 className="text-xl font-bold text-slate-900 mt-10 mb-3">
    {no} <span className="font-normal text-slate-400">· {en}</span>
  </h2>
);

const P = ({ children }) => (
  <p className="text-slate-600 leading-relaxed mb-4">{children}</p>
);

const Ul = ({ children }) => (
  <ul className="list-disc pl-5 text-slate-600 leading-relaxed mb-4 space-y-2">{children}</ul>
);

// English translation of the section above it. Same content, quieter styling.
const En = ({ children }) => (
  <div className="border-l-2 border-slate-200 pl-4 mb-6 mt-2 [&>p]:text-slate-500 [&>p:last-child]:mb-0 [&>ul]:text-slate-500 [&>ul:last-child]:mb-0">
    <span className="inline-block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
      English
    </span>
    {children}
  </div>
);

const A = ({ href, children }) => (
  <a href={href} className="text-indigo-600 hover:underline">
    {children}
  </a>
);

const KtrlPrivacyPage = () => {
  return (
    <KtrlLayout>
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Tag>Personvernerklæring · Privacy Policy</Tag>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
          Personvernerklæring · Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-10">
          Sist oppdatert · Last updated: 20. september 2026
        </p>

        <P>
          Denne siden er KTRL AS' personvernerklæring. Den forklarer hvilke personopplysninger vi
          behandler, hvorfor, hvordan de beskyttes, hvor lenge de lagres, og hvordan du kan be om at
          de slettes. Erklæringen dekker to ting: (1) nettstedet ktrl.no og KTRLs egne kontakter, og
          (2) <strong>Swarm</strong>, KTRLs AI-drevne markedsføringsplattform, som med kundens
          uttrykkelige samtykke kobler seg til kundens egen Google Ads-konto.
        </P>
        <En>
          <P>
            This is the privacy policy of KTRL AS. It explains what personal data we process, why,
            how it is protected, how long it is kept, and how you can ask for it to be deleted. It
            covers two things: (1) the ktrl.no website and KTRL's own contacts, and (2){" "}
            <strong>Swarm</strong>, KTRL's AI-driven marketing platform, which connects to a
            customer's own Google Ads account with that customer's explicit consent.
          </P>
        </En>

        <H2 no="Behandlingsansvarlig" en="Data controller" />
        <P>
          KTRL AS (org.nr. 827 430 242), Norge, er behandlingsansvarlig for personopplysninger som
          samles inn gjennom ktrl.no. Har du spørsmål om personvern eller vil utøve rettighetene
          dine, kontakt oss på <A href="mailto:jakob@ktrl.no">jakob@ktrl.no</A> eller{" "}
          <A href="tel:+4790639637">+47 906 39 637</A>.
        </P>
        <En>
          <P>
            KTRL AS (company no. 827 430 242), Norway, is the data controller for personal data
            collected through ktrl.no. For privacy questions or to exercise your rights, contact{" "}
            <A href="mailto:jakob@ktrl.no">jakob@ktrl.no</A> or{" "}
            <A href="tel:+4790639637">+47 906 39 637</A>.
          </P>
        </En>

        <H2 no="Hvilke opplysninger vi samler inn" en="What data we collect" />
        <Ul>
          <li>
            <strong>Opplysninger du gir oss direkte:</strong> tar du kontakt via e-post eller
            telefon, mottar vi det du selv oppgir — typisk navn, e-postadresse, telefonnummer og
            innholdet i henvendelsen.
          </li>
          <li>
            <strong>E-postadresse ved nedlasting av CV:</strong> på cv.ktrl.no kan du oppgi
            e-postadressen din for å laste ned en CV. Adressen og tidspunktet lagres hos oss slik at
            vi vet hvem som har bedt om dokumentet.
          </li>
          <li>
            <strong>Serverlogger:</strong> som på de fleste nettsteder registreres standard
            serverlogger (IP-adresse, tidspunkt, nettleser- og enhetstype) av sikkerhets- og
            driftshensyn.
          </li>
          <li>
            <strong>Tredjeparter:</strong> nettsiden laster skrifttyper fra Google Fonts, slik at
            nettleseren din kobler til Googles servere, som da kan motta IP-adressen din. Vi bruker
            ikke informasjonskapsler (cookies) for sporing, analyse eller markedsføring på ktrl.no.
          </li>
        </Ul>
        <En>
          <Ul>
            <li>
              <strong>Data you give us directly:</strong> if you contact us by email or phone, we
              receive what you choose to provide — typically name, email address, phone number and
              the content of your enquiry.
            </li>
            <li>
              <strong>Email address for CV downloads:</strong> on cv.ktrl.no you can enter your
              email address to download a CV. The address and timestamp are stored so we know who
              requested the document.
            </li>
            <li>
              <strong>Server logs:</strong> as on most websites, standard server logs (IP address,
              timestamp, browser and device type) are recorded for security and operations.
            </li>
            <li>
              <strong>Third parties:</strong> the site loads fonts from Google Fonts, so your
              browser connects to Google's servers, which may receive your IP address. We use no
              cookies for tracking, analytics or advertising on ktrl.no.
            </li>
          </Ul>
        </En>

        <H2 no="Hvorfor vi behandler opplysningene" en="Why we process it" />
        <P>
          Opplysningene du gir oss brukes utelukkende til å besvare henvendelsen din og følge opp en
          eventuell dialog om våre tjenester. Serverlogger brukes til å drifte og sikre nettstedet
          og til å forebygge misbruk. Det rettslige grunnlaget er vår berettigede interesse i å
          drifte et sikkert nettsted, og — når du kontakter oss — å besvare henvendelsen og
          eventuelt inngå en avtale.
        </P>
        <En>
          <P>
            Data you give us is used solely to answer your enquiry and follow up any dialogue about
            our services. Server logs are used to operate and secure the site and to prevent abuse.
            The legal basis is our legitimate interest in running a secure website and, when you
            contact us, answering your enquiry and potentially entering into an agreement.
          </P>
        </En>

        <H2 no="Swarm og data fra Google API-er" en="Swarm and data from Google APIs" />
        <P>
          Swarm er en plattform KTRL utvikler og drifter for markedsførings- og annonsekunder. Når
          en kunde velger å koble Swarm til sin egen Google Ads-konto, gir kunden samtykke gjennom
          Googles egen OAuth-dialog. Swarm ber om tilgangsområdet (scope){" "}
          <code className="text-sm">https://www.googleapis.com/auth/adwords</code>, som Google
          klassifiserer som et sensitivt tilgangsområde. Swarm ber <strong>ikke</strong> om tilgang
          til Gmail, Google Drive, Kalender, Kontakter, Bilder eller andre personlige Google-data.
        </P>
        <P>
          <strong>Hvilke data vi henter:</strong> kampanje-, annonsegruppe-, søkeord-, konverterings-
          og resultatdata fra kundens egen Google Ads-konto, samt kontoens navn og ID.{" "}
          <strong>Hva vi bruker dem til:</strong> utelukkende til å levere funksjoner kunden ser og
          bruker i Swarm — rapporter, analyser, anbefalinger og endringer kunden selv iverksetter i
          sine annonsekampanjer. Vi bruker ikke disse dataene til å profilere enkeltpersoner, til vår
          egen markedsføring, eller til å trene generelle AI-modeller.
        </P>
        <P>
          <strong>Hva vi aldri gjør med Google-data:</strong> vi selger dem ikke, og vi deler dem
          ikke med datameglere eller informasjonsformidlere. Vi bruker dem ikke til målrettet
          annonsering, personalisert eller retargetet annonsering, eller til vurderinger av
          kredittverdighet eller lån. Vi bruker dem ikke til å utvikle, forbedre eller trene
          generaliserte eller ikke-personaliserte AI- eller maskinlæringsmodeller. Dataene kan
          behandles av våre AI-modelleverandører utelukkende for å produsere analysen kunden selv
          har bedt om i Swarm, og leverandørene bruker dem ikke til å trene modellene sine.
        </P>
        <P>
          <strong>Menneskelig innsyn:</strong> ingen hos KTRL leser en kundes Google-data uten at
          kunden uttrykkelig har bedt om det (for eksempel ved support), det er nødvendig av
          sikkerhetshensyn, eller vi er rettslig forpliktet til det.
        </P>
        <P>
          <strong>Sletting og tilbaketrekking:</strong> kunden kan når som helst trekke tilbake
          Swarms tilgang på{" "}
          <A href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</A>.
          Da stopper all videre henting av data umiddelbart. Lagrede tilgangsnøkler og synkroniserte
          Google-data slettes når kundeforholdet avsluttes, eller innen 30 dager etter en
          sletteforespørsel til <A href="mailto:jakob@ktrl.no">jakob@ktrl.no</A>.
        </P>
        <P>
          Swarms bruk og overføring av informasjon mottatt fra Google API-er følger{" "}
          <A href="https://developers.google.com/terms/api-services-user-data-policy#limited-use">
            Google API Services User Data Policy
          </A>
          , inkludert kravene om begrenset bruk (Limited Use).
        </P>
        <En>
          <P>
            Swarm is a platform KTRL builds and operates for marketing and advertising customers.
            When a customer chooses to connect Swarm to their own Google Ads account, they consent
            through Google's own OAuth dialog. Swarm requests the scope{" "}
            <code className="text-sm">https://www.googleapis.com/auth/adwords</code>, which Google
            classifies as a sensitive scope. Swarm does <strong>not</strong> request access to
            Gmail, Google Drive, Calendar, Contacts, Photos or any other personal Google data.
          </P>
          <P>
            <strong>What we access:</strong> campaign, ad group, keyword, conversion and performance
            data from the customer's own Google Ads account, plus the account name and ID.{" "}
            <strong>What we use it for:</strong> solely to provide features the customer sees and
            uses in Swarm — reports, analysis, recommendations, and changes the customer themselves
            initiates on their advertising campaigns. We do not use this data to profile
            individuals, for our own marketing, or to train general AI models.
          </P>
          <P>
            <strong>What we never do with Google data:</strong> we do not sell it, and we do not
            share it with data brokers or information resellers. We do not use it for targeted
            advertising, personalised or retargeted advertising, or for determining credit-
            worthiness or for lending purposes. We do not use it to develop, improve or train
            generalised or non-personalised AI or machine learning models. The data may be processed
            by our AI model providers solely to produce the analysis the customer asked for in
            Swarm, and those providers do not use it to train their models.
          </P>
          <P>
            <strong>Human access:</strong> nobody at KTRL reads a customer's Google data unless the
            customer has expressly asked us to (for example for support), it is necessary for
            security, or we are legally required to.
          </P>
          <P>
            <strong>Deletion and revocation:</strong> a customer can revoke Swarm's access at any
            time at{" "}
            <A href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</A>,
            which immediately stops any further data retrieval. Stored credentials and synchronised
            Google data are deleted when the customer relationship ends, or within 30 days of a
            deletion request to <A href="mailto:jakob@ktrl.no">jakob@ktrl.no</A>.
          </P>
          <P>
            Swarm's use and transfer of information received from Google APIs to any other app will
            adhere to the{" "}
            <A href="https://developers.google.com/terms/api-services-user-data-policy#limited-use">
              Google API Services User Data Policy
            </A>
            , including the Limited Use requirements.
          </P>
        </En>

        <H2 no="Hvordan vi beskytter dataene" en="How we protect data" />
        <P>
          Vi bruker følgende tekniske og organisatoriske tiltak for å beskytte personopplysninger og
          data hentet fra Google API-er — inkludert data fra tilgangsområder Google klassifiserer
          som sensitive:
        </P>
        <Ul>
          <li>
            <strong>Kryptering under overføring:</strong> all trafikk til og fra våre nettsteder og
            tjenester går over HTTPS med TLS 1.3 og gyldige sertifikater. All kommunikasjon med
            Googles API-er skjer over TLS.
          </li>
          <li>
            <strong>Kryptering av tilgangsnøkler:</strong> Google-legitimasjon (refresh token,
            developer token, konto-ID) lagres kryptert med Fernet (AES-128-CBC med HMAC-SHA256
            integritetsbeskyttelse). Access tokens lagres aldri — de utveksles ved behov for hvert
            kall.
          </li>
          <li>
            <strong>Personopplysninger lagres ikke i klartekst:</strong> personopplysningsfelter i
            Swarm krypteres, og oppslag skjer via SHA-256-hasher i stedet for klartekstverdier.
          </li>
          <li>
            <strong>Streng tilgangskontroll:</strong> produksjonsmiljøet driftes av én navngitt
            person i KTRL. Serveradgang skjer over kryptert SSH med personlige nøkler, og vi har
            ingen delte administratorkontoer.
          </li>
          <li>
            <strong>Revisjonslogg:</strong> handlinger som utføres i Swarm — inkludert endringer på
            en kundes annonsekonto — skrives til en revisjonslogg, slik at det i ettertid kan spores
            hva som ble gjort og hvorfor.
          </li>
          <li>
            <strong>Dataminimering:</strong> vi henter kun de datafeltene funksjonene faktisk
            trenger, og lagrer dem ikke lenger enn nødvendig.
          </li>
          <li>
            <strong>Plassering i EU/EØS:</strong> våre servere driftes hos Hetzner i Helsinki,
            Finland (EU/EØS).
          </li>
          <li>
            <strong>Avvikshåndtering:</strong> ved et brudd på personopplysningssikkerheten varsler
            vi Datatilsynet innen 72 timer, og berørte personer eller kunder uten ugrunnet opphold,
            i tråd med GDPR artikkel 33 og 34.
          </li>
        </Ul>
        <En>
          <P>
            We use the following technical and organisational measures to protect personal data and
            data obtained from Google APIs — including data from scopes Google classifies as
            sensitive:
          </P>
          <Ul>
            <li>
              <strong>Encryption in transit:</strong> all traffic to and from our sites and services
              uses HTTPS with TLS 1.3 and valid certificates. All communication with Google's APIs
              is over TLS.
            </li>
            <li>
              <strong>Encrypted credentials:</strong> Google credentials (refresh token, developer
              token, account ID) are stored encrypted with Fernet (AES-128-CBC with HMAC-SHA256
              integrity protection). Access tokens are never stored — they are exchanged as needed
              for each call.
            </li>
            <li>
              <strong>No personal data in plaintext:</strong> personal data fields in Swarm are
              encrypted, and lookups use SHA-256 hashes rather than plaintext values.
            </li>
            <li>
              <strong>Strict access control:</strong> the production environment is operated by one
              named person at KTRL. Server access is over encrypted SSH with personal keys, and we
              use no shared administrator accounts.
            </li>
            <li>
              <strong>Audit log:</strong> actions taken in Swarm — including changes to a customer's
              advertising account — are written to an audit log, so what was done and why can be
              traced afterwards.
            </li>
            <li>
              <strong>Data minimisation:</strong> we retrieve only the fields the features actually
              need, and keep them no longer than necessary.
            </li>
            <li>
              <strong>Hosted in the EU/EEA:</strong> our servers run at Hetzner in Helsinki, Finland
              (EU/EEA).
            </li>
            <li>
              <strong>Breach handling:</strong> in the event of a personal data breach we notify the
              Norwegian Data Protection Authority within 72 hours, and affected individuals or
              customers without undue delay, per GDPR articles 33 and 34.
            </li>
          </Ul>
        </En>

        <H2 no="Særlige kategorier av personopplysninger" en="Special categories of personal data" />
        <P>
          KTRL ber ikke om, og har ikke behov for, særlige kategorier av personopplysninger
          («sensitive personopplysninger» etter GDPR artikkel 9) — som helseopplysninger,
          biometriske data, religiøs eller politisk overbevisning — verken på ktrl.no eller i Swarm.
          Google Ads-tilgangen Swarm bruker er klassifisert som et sensitivt tilgangsområde hos
          Google fordi den gir tilgang til en kundes annonsekonto; den gir ikke tilgang til
          helseopplysninger eller lignende. Data fra dette tilgangsområdet beskyttes som beskrevet
          over.
        </P>
        <En>
          <P>
            KTRL does not request, and has no need for, special categories of personal data
            ("sensitive personal data" under GDPR article 9) — such as health data, biometric data,
            or religious or political beliefs — neither on ktrl.no nor in Swarm. The Google Ads
            access Swarm uses is classified by Google as a sensitive scope because it grants access
            to a customer's advertising account; it does not grant access to health data or
            anything similar. Data from this scope is protected as described above.
          </P>
        </En>

        <H2 no="Hvor lenge vi lagrer opplysningene" en="How long we keep data" />
        <P>
          Henvendelser og korrespondanse lagres så lenge det er nødvendig for å følge opp saken din,
          og slettes senest 24 måneder etter siste kontakt, med mindre vi er lovpålagt å lagre dem
          lenger (for eksempel regnskapsbilag). E-postadresser fra CV-nedlasting slettes senest 24
          måneder etter nedlastingen. Data hentet fra en kundes Google Ads-konto lagres så lenge
          koblingen er aktiv, og slettes som beskrevet i avsnittet om Swarm.
        </P>
        <En>
          <P>
            Enquiries and correspondence are kept as long as needed to follow up your matter, and
            deleted no later than 24 months after the last contact, unless we are legally required
            to keep them longer (accounting records, for example). Email addresses from CV downloads
            are deleted no later than 24 months after the download. Data retrieved from a
            customer's Google Ads account is kept while the connection is active and deleted as
            described in the Swarm section.
          </P>
        </En>

        <H2 no="Deling av opplysninger" en="Sharing of data" />
        <P>
          Vi selger aldri personopplysninger, og vi overfører dem ikke til tredjeparter for deres
          egne formål. Opplysninger deles kun med underleverandører som er nødvendige for driften,
          og kun i den grad formålet krever det: vår serverleverandør (Hetzner, EU/EØS), vår
          e-postleverandør (Domeneshop, Norge), og leverandører av AI-modeller som brukes til å
          produsere analysene i Swarm. Alle er bundet av databehandleravtaler i tråd med GDPR, og
          data som sendes til AI-modelleverandører brukes ikke til å trene modellene deres.
        </P>
        <En>
          <P>
            We never sell personal data, and we do not transfer it to third parties for their own
            purposes. Data is shared only with the sub-processors needed to run the service, and
            only as far as the purpose requires: our server provider (Hetzner, EU/EEA), our email
            provider (Domeneshop, Norway), and the providers of the AI models used to produce
            Swarm's analyses. All are bound by data processing agreements under GDPR, and data sent
            to AI model providers is not used to train their models.
          </P>
        </En>

        <H2
          no="Når KTRL leverer AI-agenter og konsulenttjenester"
          en="When KTRL delivers AI agents and consulting"
        />
        <P>
          Når vi bygger eller drifter AI-drevne systemer for en kunde — for eksempel en agent som
          besvarer kundens egne sluttbrukere, eller Swarm koblet til kundens annonsekonto —
          behandler vi personopplysninger i det systemet på vegne av kunden, regulert av en egen
          databehandleravtale mellom KTRL og kunden. I slike tilfeller er det kunden, ikke KTRL, som
          er behandlingsansvarlig for sluttbrukernes personopplysninger.
        </P>
        <En>
          <P>
            When we build or operate AI-driven systems for a customer — for example an agent that
            answers the customer's own end users, or Swarm connected to their advertising account —
            we process personal data in that system on the customer's behalf, governed by a separate
            data processing agreement between KTRL and the customer. In those cases the customer,
            not KTRL, is the data controller for end users' personal data.
          </P>
        </En>

        <H2 no="Dine rettigheter" en="Your rights" />
        <P>
          Du har etter personvernforordningen (GDPR) rett til innsyn i, retting av, sletting av og
          begrensning av opplysninger vi har om deg, samt rett til dataportabilitet og rett til å
          protestere mot behandlingen. Du har også rett til å klage til Datatilsynet
          (datatilsynet.no) dersom du mener vi behandler opplysningene dine i strid med regelverket.
        </P>
        <En>
          <P>
            Under the GDPR you have the right to access, rectification, erasure and restriction of
            data we hold about you, as well as the right to data portability and the right to object
            to processing. You also have the right to complain to the Norwegian Data Protection
            Authority (datatilsynet.no) if you believe we process your data unlawfully.
          </P>
        </En>

        <H2 no="Slik ber du om sletting" en="How to request deletion" />
        <P>
          Send en e-post til{" "}
          <A href="mailto:jakob@ktrl.no?subject=Sletting%20av%20personopplysninger">
            jakob@ktrl.no
          </A>{" "}
          med emnet «Sletting av personopplysninger». Vi bekrefter mottak og sletter opplysningene
          innen 30 dager, med mindre vi har en lovpålagt plikt til å beholde dem lenger. Gjelder
          forespørselen data i Swarm, sletter vi lagrede tilgangsnøkler og synkroniserte
          Google-data innen samme frist.
        </P>
        <En>
          <P>
            Send an email to{" "}
            <A href="mailto:jakob@ktrl.no?subject=Deletion%20of%20personal%20data">
              jakob@ktrl.no
            </A>{" "}
            with the subject "Deletion of personal data". We confirm receipt and delete the data
            within 30 days, unless we are legally required to keep it longer. If the request
            concerns data in Swarm, we delete stored credentials and synchronised Google data within
            the same deadline.
          </P>
        </En>

        <H2 no="Endringer i personvernerklæringen" en="Changes to this policy" />
        <P>
          Vi kan oppdatere denne personvernerklæringen ved behov, for eksempel når vi endrer hvordan
          tjenestene våre fungerer. Datoen øverst viser når den sist ble oppdatert. Vesentlige
          endringer vil bli tydelig markert her.
        </P>
        <En>
          <P>
            We may update this privacy policy when needed, for example when we change how our
            services work. The date at the top shows when it was last updated. Material changes will
            be clearly marked here.
          </P>
        </En>
      </section>
    </KtrlLayout>
  );
};

export default KtrlPrivacyPage;
